import { Outlet, Navigate, useLocation, Link } from "react-router-dom";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "./AppSidebar";
import { useAuth } from "@/hooks/useAuth";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useAppSettings } from "@/hooks/useAppSettings";
import { Skeleton } from "@/components/ui/skeleton";
import { PaymentTestModeBanner } from "./PaymentTestModeBanner";
import { ThemeToggle } from "./ThemeToggle";
import { Megaphone } from "lucide-react";

export default function AppLayout() {
  const { user, loading } = useAuth();
  const { isAdmin, loading: roleLoading } = useIsAdmin();
  const { ads } = useAppSettings();
  const { pathname } = useLocation();

  if (loading || roleLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-subtle">
        <Skeleton className="h-12 w-48" />
      </div>
    );
  }
  if (!user) return <Navigate to="/auth" replace />;

  // Admins live in /admin; redirect them away from customer pages.
  if (isAdmin && !pathname.startsWith("/admin")) {
    return <Navigate to="/admin" replace />;
  }

  const showBanner = !isAdmin && ads.enabled && !!ads.message;

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-gradient-subtle">
        <AppSidebar />
        <div className="flex-1 flex flex-col min-w-0">
          <PaymentTestModeBanner />
          {showBanner && (
            <div className="bg-gradient-primary text-primary-foreground px-4 py-2 text-sm flex items-center justify-center gap-3 flex-wrap">
              <Megaphone className="h-4 w-4 shrink-0" />
              <span>{ads.message}</span>
              {ads.ctaLabel && ads.ctaUrl && (
                <Link
                  to={ads.ctaUrl}
                  className="underline font-medium hover:opacity-90"
                >
                  {ads.ctaLabel}
                </Link>
              )}
            </div>
          )}
          <header className="h-14 flex items-center gap-2 border-b bg-background/80 backdrop-blur px-4 sticky top-0 z-10">
            <SidebarTrigger />
            <div className="ml-auto relative"><ThemeToggle /></div>
          </header>
          <main className="flex-1 p-4 md:p-8">
            <Outlet />
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}

